import {
  Button,
  ButtonColorEnum,
  ChevronLeftIcon,
  ClockIcon,
  CloseIcon,
  EraseContentIcon,
  FontColorEnum,
  FontSizeEnum,
  FontWeightEnum,
  GoToOutIcon,
  IconButton,
  IconButtonColorEnum,
  IconButtonSizeEnum,
  IconSizeEnum,
  ModalDialog,
  ModalMessage,
  ModalMessageColorsEnum,
  Tooltip,
  Typography,
  WarningInfoIcon,
} from '@acceso/design-system';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { v4 as uuiv4 } from 'uuid';

import { FooterFormActions } from '@/components/FooterFormActions/FooterFormActions';
import { InputArea } from '@/components/FormFields/InputArea';
import { InputDate } from '@/components/FormFields/InputDate';
import { RowContainer } from '@/components/FormFields/RowContainer';
import { SelectForm } from '@/components/FormFields/SelectForm';
import { StatusSeenChipSelect } from '@/components/StatusSeenChipSelect';
import { ToolTipContent } from '@/components/ToolTipContent';
import { SEGMENT_REVIEW_STATES } from '@/constants';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { cutOverlapsBroadcast } from '@/features/broadcast-schedule-program/helpers/cut-overlaps-broadcast';
import { hasEnrichmentsOutsideCut } from '@/features/broadcast-schedule-program/helpers/has-enrichments-outside-cut';
import { isPresenceAddDisabled } from '@/features/broadcast-schedule-program/helpers/is-presence-add-disabled';
import { isStatementAddDisabled } from '@/features/broadcast-schedule-program/helpers/is-statement-add-disabled';
import { mergePreviewEnrichments } from '@/features/broadcast-schedule-program/helpers/merge-preview-enrichments';
import { withResolvedImages } from '@/features/broadcast-schedule-program/helpers/with-resolved-images';
import { useCutFormShortcuts } from '@/features/broadcast-schedule-program/hooks/useCutFormShortcuts';
import { useNavigationList } from '@/features/broadcast-schedule-program/hooks/useNavigationList';
import { TToolkitComponentProps } from '@/features/broadcast-schedule-program/types';
import { useChannelId } from '@/features/broadcasters/api/get-channel-id';
import { ChannelMediaTypeEnum } from '@/features/broadcasters/types';
import { useDeleteClipDeclaration } from '@/features/clips/api/delete-clips-declaration';
import { useDeleteClipLogo } from '@/features/clips/api/delete-clips-logos';
import { useDeleteClipPeople } from '@/features/clips/api/delete-clips-people';
import { useGetClipsCategories } from '@/features/clips/api/get-clip-categories';
import { useGetPreviewEnrichments } from '@/features/clips/api/get-preview-enrichments';
import { usePatchClip } from '@/features/clips/api/patch-clips';
import { usePatchClipLogo } from '@/features/clips/api/patch-clips-logos';
import { usePatchClipPeople } from '@/features/clips/api/patch-clips-people';
import { usePostClip } from '@/features/clips/api/post-clips';
import { usePostClipDeclaration } from '@/features/clips/api/post-clips-declaration';
import { usePostClipLogo } from '@/features/clips/api/post-clips-logos';
import { usePostClipPeople } from '@/features/clips/api/post-clips-people';
import { TPostClip } from '@/features/clips/types';
import { TFeaturedPeopleType } from '@/features/featured-people/types';
import { TLogo } from '@/features/logos/types';
import { TReviewStateValues, TSegment, TSegmentMention } from '@/features/segments/types';
import { formatDate, getDurationInSecondsBetweenStartDateAndEndDate } from '@/helpers/format-date';
import { useLogosById, usePeopleById } from '@/hooks/use-catalogue-entries-by-id';
import { useDisclosure } from '@/hooks/useDisclosure';
import { I18nextTranslationKeys } from '@/i18n/i18next';
import { queryClient } from '@/lib/react-query';
import { useNotificationStore } from '@/stores/notifications';
import { convertDatesToSeconds, getAbsoluteDateFromPlayerTime } from '@/utils/hh-mm-ss-into-number';

import { AddLogos } from '../../../../AddLogos/AddLogos';
import { AddPeople } from '../../../../AddPeople/AddPeople';
import { AddStatements } from '../../../../AddStatements/AddStatements';
import { ShowMentions } from '../../../../ShowMentions/ShowMentions';
import { CUT_FORM_DEFAULT_VALUES } from '../constants';
import {
  TBroadcastScheduleProgramBodySegmentToolkitCutForm,
  TSegmentDeclarationClip,
  TSegmentLogoClip,
  TSegmentPeopleClip,
} from '../types';

import { ProgramSelector } from './ProgramSelector';

// RISK: a fresh `[]` default would re-run the thumbnail effect on every render, looping until
// the names arrive.
const NO_LOGOS: TLogo[] = [];
const NO_PEOPLE: TFeaturedPeopleType[] = [];

function getSegmentsForClipByDate(
  segments: TSegment[] = [],
  clipStart?: string | Date,
  clipEnd?: string | Date
): TSegment[] {
  const startDate = clipStart instanceof Date ? clipStart : clipStart ? new Date(clipStart) : null;
  const endDate = clipEnd instanceof Date ? clipEnd : clipEnd ? new Date(clipEnd) : null;

  if (!segments.length || !startDate || !endDate) return [];

  const clipStartTime = startDate.getTime() / 1000;
  const clipEndTime = endDate.getTime() / 1000;

  return segments.filter(segment => {
    const segmentStart = new Date(segment.start_datetime).getTime() / 1000;
    const segmentEnd = segmentStart + segment.duration_in_seconds;

    return segmentEnd >= clipStartTime && segmentStart <= clipEndTime;
  });
}

type TBroadcastScheduleProgramBodySegmentToolkitCutFormProps = TToolkitComponentProps & {
  onAfterSave?: () => void;
};

export function BroadcastScheduleProgramBodySegmentToolkitCutForm({
  programBroadcast,
  onAfterSave,
}: TBroadcastScheduleProgramBodySegmentToolkitCutFormProps) {
  const {
    cutData,
    activeSegment,
    playerVideoCurrentTime,
    selectedMention,
    segmentItems,
    isParrillaSegments,
    cutForm,
    setCutData,
    setFormBox,
    setSelectedMention,
    setActiveSegment,
    handleDateTimeSegment,
    setPendingSeekTime,
    videoControlRef,
  } = useBroadcastScheduleProgram();
  const { t } = useTranslation();
  const { data: cutChannel, isError: isCutChannelError } = useChannelId({
    id: isParrillaSegments ? programBroadcast?.channel_id : activeSegment?.channel_id,
  });
  const { data: clipCategories = [] } = useGetClipsCategories();
  const { mutateAsync: createClip } = usePostClip();
  const { mutateAsync: updateClip } = usePatchClip();
  const { mutateAsync: createClipLogo } = usePostClipLogo();
  const { mutateAsync: deleteClipLogo } = useDeleteClipLogo();
  const { mutateAsync: patchClipLogo } = usePatchClipLogo();
  const { mutateAsync: createClipPeople } = usePostClipPeople();
  const { mutateAsync: deleteClipPeople } = useDeleteClipPeople();
  const { mutateAsync: patchClipPeople } = usePatchClipPeople();
  const { mutateAsync: createClipDeclaration } = usePostClipDeclaration();
  const { mutateAsync: deleteClipDeclaration } = useDeleteClipDeclaration();
  const previewModal = useDisclosure();
  const { addErrorNotification } = useNotificationStore();

  const [mentions, setMentions] = useState<TSegmentMention[]>([]);
  const [statements, setStatements] = useState<TSegmentDeclarationClip[]>([]);
  const [logos, setLogos] = useState<TSegmentLogoClip[]>([]);
  const [people, setPeople] = useState<TSegmentPeopleClip[]>([]);
  const lastSeekCutIdRef = useRef<string | null>(null);
  const [segmentsByClip, setSegmentsByClip] = useState<TSegment[]>([]);
  const [showStatementSelect, setShowStatementSelect] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const clipCategoriesOptions = useMemo(() => {
    return clipCategories.map(category => ({
      label: category.name,
      value: category.id,
    }));
  }, [clipCategories]);

  const statusValue = useWatch({
    control: cutForm.control,
    name: 'review_state',
  });

  const startTimeValue = useWatch({
    control: cutForm.control,
    name: 'start_datetime',
  });
  const endTimeValue = useWatch({
    control: cutForm.control,
    name: 'end_datetime',
  });
  const programId = useWatch({
    control: cutForm.control,
    name: 'program_id',
  });

  const effectiveProgramId = isParrillaSegments
    ? programBroadcast?.program_id || ''
    : programId || '';

  // Only the emission being reviewed is known here, so the cut window can just
  // be checked against it when the form targets that same program.
  const broadcastWindow =
    programBroadcast && programBroadcast.program_id === effectiveProgramId
      ? programBroadcast
      : null;

  const broadcastRangeLabel = broadcastWindow
    ? `${formatDate(broadcastWindow.start_datetime).timePart} - ${formatDate(broadcastWindow.end_datetime).timePart}`
    : '';

  const layoutMediaType = cutData ? cutData.media_type : cutChannel?.media_type;
  const showTvSections =
    !!layoutMediaType &&
    layoutMediaType !== ChannelMediaTypeEnum.RADIO &&
    layoutMediaType !== ChannelMediaTypeEnum.AUDIO_PODCAST;
  const isNewCutWithoutChannel = !cutData && !cutChannel;

  const showWarningStatements = useMemo(
    () => hasEnrichmentsOutsideCut(statements, startTimeValue, endTimeValue),
    [statements, startTimeValue, endTimeValue]
  );

  const showWarningLogos = useMemo(
    () => hasEnrichmentsOutsideCut(logos, startTimeValue, endTimeValue),
    [logos, startTimeValue, endTimeValue]
  );

  const showWarningPeople = useMemo(
    () => hasEnrichmentsOutsideCut(people, startTimeValue, endTimeValue),
    [people, startTimeValue, endTimeValue]
  );

  const [shouldFetchPreview, setShouldFetchPreview] = useState<boolean>(false);

  const { data: previewData = null, isFetching: isLoadingPreview } = useGetPreviewEnrichments({
    filters: {
      program_id: effectiveProgramId,
      start_datetime: cutForm.getValues('start_datetime')?.toISOString(),
      end_datetime: cutForm.getValues('end_datetime')?.toISOString(),
      shouldFetchPreview,
    },
  });

  const {
    query: { data: { data: allLogos = NO_LOGOS } = {} },
  } = useLogosById([
    ...(cutData?.clip_logos ?? []).map(clipLogo => clipLogo.logo_id),
    ...(previewData?.clip_logos ?? []).map(clipLogo => clipLogo.logo_id),
  ]);
  const {
    query: { data: { data: allPeople = NO_PEOPLE } = {} },
  } = usePeopleById([
    ...(cutData?.clip_peoples ?? []).map(clipPeople => clipPeople.people_id),
    ...(previewData?.clip_peoples ?? []).map(clipPeople => clipPeople.people_id),
  ]);

  useEffect(() => {
    if (cutData?.id) {
      setShouldFetchPreview(false);
      return;
    }

    const hasProgramId = programBroadcast?.program_id || programId;
    if (hasProgramId && startTimeValue && endTimeValue) {
      setShouldFetchPreview(true);
    }
  }, [cutData?.id, programBroadcast?.program_id, programId, startTimeValue, endTimeValue]);

  const handleUpdateEnrichments = () => {
    setShouldFetchPreview(true);
  };

  useEffect(() => {
    if (shouldFetchPreview && previewData && !isLoadingPreview) {
      setShouldFetchPreview(false);
    }
  }, [shouldFetchPreview, previewData, isLoadingPreview]);

  const handleClickExit = useCallback(() => {
    setFormBox(null);
    setCutData(null);
    setSelectedMention('');
    cutForm.reset(CUT_FORM_DEFAULT_VALUES);
  }, [setFormBox, setCutData]);

  const handleCreate = useCallback(
    async (newBody: TPostClip) => {
      try {
        const clipData = await createClip(newBody);
        const requests: Promise<unknown>[] = [];

        if (logos && logos.length > 0) {
          const logoPromises = logos.map(logo =>
            createClipLogo({
              clip_id: clipData.id || '',
              logo_id: logo.logo_id as string,
              parameters: {
                bbox: logo.parameters?.bbox || [0, 0, 0, 0],
              },
              start_datetime: logo.s_datetime || null,
              end_datetime: logo.e_datetime || null,
              entity_score: 1,
              review_state: 1,
              silently: true,
            })
          );
          requests.push(...logoPromises);
        }

        if (people && people.length > 0) {
          const peoplePromises = people.map(person =>
            createClipPeople({
              clip_id: clipData.id || '',
              people_id: person.people_id as string,
              parameters: {
                bbox: person.parameters?.bbox || [0, 0, 0, 0],
              },
              start_datetime: person.s_datetime || null,
              end_datetime: person.e_datetime || null,
              entity_score: 1,
              review_state: 1,
              silently: true,
            })
          );
          requests.push(...peoplePromises);
        }
        if (statements && statements.length > 0) {
          const statementPromises = statements.map(statement =>
            createClipDeclaration({
              clip_id: clipData.id || '',
              text: statement.text,
              start_datetime: statement.s_datetime || null,
              end_datetime: statement.s_datetime || null,
              people_id: statement.people_id,
              entity_label: statement.entity_label || null,
              review_state: 1,
              entity_score: 1,
              silently: true,
            })
          );
          requests.push(...statementPromises);
        }

        await Promise.all(requests);
        queryClient.invalidateQueries({ queryKey: ['clips'] });
        handleClickExit();
      } catch (error) {
        console.error(error);
      }
    },
    [createClip, createClipLogo, createClipPeople, logos, people, statements, handleClickExit]
  );

  const handleUpdate = useCallback(
    async (newBody: TPostClip) => {
      try {
        const clipData = await updateClip(newBody as any);

        const originalLogos: TSegmentLogoClip[] = cutData?.clip_logos || [];
        const originalPeople: TSegmentPeopleClip[] = cutData?.clip_peoples || [];
        const originalStatements: TSegmentDeclarationClip[] = cutData?.clip_declarations || [];

        const logosToAdd = logos.filter(logo => !originalLogos.some(orig => orig.id === logo.id));
        const logosToDelete = originalLogos.filter(
          orig => !logos.some(logo => logo.id === orig.id)
        );
        const logosToUpdate = logos.filter(logo => {
          const orig = originalLogos.find(o => o.id === logo.id);
          return orig && orig.logo_id !== logo.logo_id;
        });

        const peopleToAdd = people.filter(
          person => !originalPeople.some(orig => orig.id === person.id)
        );
        const peopleToDelete = originalPeople.filter(
          orig => !people.some(person => person.id === orig.id)
        );
        const peopleToUpdate = people.filter(person => {
          const orig = originalPeople.find(o => o.id === person.id);
          return orig && orig.people_id !== person.people_id;
        });

        const statementsToAdd = statements.filter(
          statement => !originalStatements.some(orig => orig.id === statement.id)
        );
        const statementsToDelete = originalStatements.filter(
          orig => !statements.some(statement => statement.id === orig.id)
        );

        const requests: Promise<unknown>[] = [];

        if (logosToAdd.length > 0) {
          const addLogoRequests = logosToAdd.map(logo =>
            createClipLogo({
              clip_id: clipData.id || '',
              logo_id: logo.logo_id as string,
              parameters: {
                bbox: logo.parameters?.bbox || [0, 0, 0, 0],
              },
              start_datetime: logo.s_datetime || null,
              end_datetime: logo.e_datetime || null,
              entity_score: 1,
              review_state: 1,
              silently: true,
            })
          );
          requests.push(...addLogoRequests);
        }
        if (logosToDelete.length > 0) {
          const deleteLogoRequests = logosToDelete.map(logo =>
            deleteClipLogo({
              id: logo.id as string,
              silently: true,
            })
          );
          requests.push(...deleteLogoRequests);
        }
        if (logosToUpdate.length > 0) {
          const updateLogoRequests = logosToUpdate.map(logo =>
            patchClipLogo({
              id: logo.id as string,
              logo_id: logo.logo_id as string,
              silently: true,
            })
          );
          requests.push(...updateLogoRequests);
        }
        if (peopleToAdd.length > 0) {
          const addPeopleRequests = peopleToAdd.map(person =>
            createClipPeople({
              clip_id: clipData.id || '',
              people_id: person.people_id as string,
              parameters: {
                bbox: person.parameters?.bbox || [0, 0, 0, 0],
              },
              start_datetime: person.s_datetime || null,
              end_datetime: person.e_datetime || null,
              entity_score: 1,
              review_state: 1,
              silently: true,
            })
          );
          requests.push(...addPeopleRequests);
        }
        if (peopleToDelete.length > 0) {
          const deletePeopleRequests = peopleToDelete.map(person =>
            deleteClipPeople({
              id: person.id as string,
              silently: true,
            })
          );
          requests.push(...deletePeopleRequests);
        }
        if (peopleToUpdate.length > 0) {
          const updatePeopleRequests = peopleToUpdate.map(person =>
            patchClipPeople({
              id: person.id as string,
              people_id: person.people_id as string,
            })
          );
          requests.push(...updatePeopleRequests);
        }
        if (statementsToAdd.length > 0) {
          const addStatementRequests = statementsToAdd.map(statement =>
            createClipDeclaration({
              clip_id: clipData.id || '',
              text: statement.text,
              start_datetime: statement.s_datetime || null,
              end_datetime: statement.s_datetime || null,
              people_id: statement.people_id,
              entity_label: statement.entity_label || null,
              review_state: 1,
              entity_score: 1,
              silently: true,
            })
          );
          requests.push(...addStatementRequests);
        }
        if (statementsToDelete.length > 0) {
          const deleteStatementRequests = statementsToDelete.map(statement =>
            deleteClipDeclaration({
              id: statement.id as string,
              silently: true,
            })
          );
          requests.push(...deleteStatementRequests);
        }

        await Promise.all(requests);
        queryClient.invalidateQueries({ queryKey: ['clips'] });
        onAfterSave?.();
      } catch (error) {
        console.error(error);
      }
    },
    [
      cutData,
      logos,
      people,
      statements,
      createClip,
      createClipLogo,
      deleteClipLogo,
      patchClipLogo,
      createClipPeople,
      deleteClipPeople,
      patchClipPeople,
      handleClickExit,
      onAfterSave,
    ]
  );

  const handleSubmit = useCallback(
    async (data: TBroadcastScheduleProgramBodySegmentToolkitCutForm) => {
      if (!data) {
        throw new Error('Invalid data');
      }

      const startSeconds = data.start_datetime;
      const endSeconds = data.end_datetime;

      if (!startSeconds || !endSeconds) {
        throw new Error('Invalid start or end date');
      }

      if (startSeconds > endSeconds) {
        alert('La fecha de inicio no puede ser posterior a la de fin');
        return;
      }

      if (hasEnrichmentsOutsideCut(statements, startSeconds, endSeconds)) {
        addErrorNotification(
          t('No se ha podido guardar el corte'),
          t('Hay declaraciones cuyo tiempo queda fuera del corte')
        );
        return;
      }

      if (hasEnrichmentsOutsideCut(logos, startSeconds, endSeconds)) {
        addErrorNotification(
          t('No se ha podido guardar el corte'),
          t('Hay logotipos cuyo tiempo queda fuera del corte')
        );
        return;
      }

      if (hasEnrichmentsOutsideCut(people, startSeconds, endSeconds)) {
        addErrorNotification(
          t('No se ha podido guardar el corte'),
          t('Hay personas cuyo tiempo queda fuera del corte')
        );
        return;
      }

      const durationInSeconds = getDurationInSecondsBetweenStartDateAndEndDate(
        new Date(startSeconds),
        new Date(endSeconds)
      );

      let newBody: TPostClip;

      if (cutData) {
        newBody = {
          id: cutData?.id,
          body: data.description,
          start_datetime: startSeconds.toISOString(),
          end_datetime: endSeconds.toISOString(),
          media_type: cutData?.media_type || 'tv',
          program_id: programId ? programId : cutData?.program_id || '',
          clip_category_id: data.seccion || cutData?.clip_category_id || '',
          review_state: statusValue,
          duration_in_seconds: durationInSeconds,
          time_keeping_state: cutData?.time_keeping_state,
        };
      } else {
        if (!cutChannel) return;

        newBody = {
          id: uuiv4(),
          body: data.description,
          start_datetime: startSeconds.toISOString(),
          end_datetime: endSeconds.toISOString(),
          media_type: cutChannel.media_type,
          program_id: effectiveProgramId,
          clip_category_id: data.seccion || '',
          review_state: statusValue,
          duration_in_seconds: durationInSeconds,
        };
      }

      setIsSubmitting(true);
      try {
        if (cutData) {
          await handleUpdate(newBody);
        } else {
          await handleCreate(newBody);
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      cutData,
      handleCreate,
      handleUpdate,
      logos,
      people,
      statements,
      cutChannel,
      activeSegment,
      programBroadcast,
      segmentItems,
      programId,
      statusValue,
      createClip,
      createClipLogo,
      createClipPeople,
    ]
  );

  useEffect(() => {
    if (cutData && segmentItems) {
      cutForm.reset({
        description: cutData.body,
        start_datetime: new Date(cutData.start_datetime) || new Date(),
        end_datetime: new Date(cutData.end_datetime) || new Date(),
        seccion: cutData.clip_category_id,
        review_state: cutData.review_state,
        program_id: cutData.program_id,
      });
      setLogos(
        withResolvedImages(
          cutData.clip_logos?.map(clip_logo => ({ ...clip_logo, tempId: uuiv4(), image: '' })) ||
            [],
          'logo_id',
          allLogos
        )
      );
      setPeople(
        withResolvedImages(
          cutData.clip_peoples?.map(clip_people => ({
            ...clip_people,
            tempId: uuiv4(),
            image: '',
          })) || [],
          'people_id',
          allPeople
        )
      );
      setStatements(
        cutData.clip_declarations?.map(clip_declaration => ({
          ...clip_declaration,
          tempId: uuiv4(),
        })) || []
      );

      const listOfSegmentsBetweenCutData = getSegmentsForClipByDate(
        segmentItems,
        cutData.start_datetime,
        cutData.end_datetime
      );

      if (listOfSegmentsBetweenCutData.length > 0) {
        const firstSeg = listOfSegmentsBetweenCutData[0];
        setActiveSegment(firstSeg);

        if (lastSeekCutIdRef.current !== cutData.id) {
          lastSeekCutIdRef.current = cutData.id;
          const seekSeconds = convertDatesToSeconds(
            cutData.start_datetime,
            firstSeg.start_datetime
          );
          setPendingSeekTime(seekSeconds);
          videoControlRef.current?.seekToSecond(seekSeconds);
        }
      }
    }
  }, [cutForm, cutData]);

  // Chip thumbnails come from the cut's logos and people resolved by id, which arrive after
  // the cut and refetch on their own — saving a declarant as a person invalidates them.
  // Patch the images in place: re-seeding from cutData here would discard every
  // enrichment the user added but has not saved yet.
  useEffect(() => {
    setLogos(prev => withResolvedImages(prev, 'logo_id', allLogos));
    setPeople(prev => withResolvedImages(prev, 'people_id', allPeople));
  }, [allLogos, allPeople]);

  const handleChangeReviewState = useCallback(
    (value: string) => {
      cutForm.setValue('review_state', parseInt(value) as TReviewStateValues);
    },
    [cutForm]
  );

  useEffect(() => {
    if (selectedMention && !showStatementSelect) {
      const currentDescription = cutForm.getValues('description') || '';
      cutForm.setValue('description', `${currentDescription} ${selectedMention}`.trim());
    }
  }, [cutForm, selectedMention, showStatementSelect]);

  useEffect(() => {
    if (startTimeValue && endTimeValue && segmentItems.length > 0) {
      const segments = getSegmentsForClipByDate(segmentItems, startTimeValue, endTimeValue);
      setSegmentsByClip(segments);
    }
  }, [startTimeValue, endTimeValue, segmentItems]);

  useEffect(() => {
    if (!previewData) return;

    const logos: TSegmentLogoClip[] =
      previewData.clip_logos.map(segment => ({
        logo_id: segment.logo_id,
        tempId: uuiv4(),
        image: allLogos.find(logoItem => logoItem.id === segment.logo_id)?.parameters
          ?.main_logo_presigned_url,
        entity_label: segment.entity_label,
        s_datetime: new Date(segment.start_datetime),
        e_datetime: new Date(segment.end_datetime),
        entity_score: segment.entity_score || 1,
        start_datetime: segment.start_datetime,
        end_datetime: segment.end_datetime,
        parameters: { ...segment.parameters },
        review_state: segment.review_state,
      })) || [];

    const people: TSegmentPeopleClip[] =
      previewData.clip_peoples.map(segment => ({
        people_id: segment.people_id,
        tempId: uuiv4(),
        image: allPeople.find(peopleItem => peopleItem.id === segment.people_id)?.parameters
          ?.main_logo_presigned_url,
        entity_label: segment.entity_label,
        s_datetime: new Date(segment.start_datetime),
        e_datetime: new Date(segment.end_datetime),
        entity_score: segment.entity_score || 1,
        start_datetime: segment.start_datetime,
        end_datetime: segment.end_datetime,
        parameters: { ...segment.parameters },
        review_state: segment.review_state,
      })) || [];

    const statements: TSegmentDeclarationClip[] =
      previewData.clip_declarations.map(segment => ({
        people_id: segment.people_id,
        text: segment.text,
        tempId: uuiv4(),
        image: allPeople.find(peopleItem => peopleItem.id === segment.people_id)?.parameters
          ?.main_logo_presigned_url,
        entity_label:
          allPeople.find(peopleItem => peopleItem.id === segment.people_id)?.name ??
          segment.entity_label,
        s_datetime: new Date(segment.start_datetime),
        e_datetime: new Date(segment.end_datetime),
        entity_score: segment.entity_score || 1,
        start_datetime: segment.start_datetime,
        end_datetime: segment.end_datetime,
        parameters: {
          ...segment.parameters,
          bbox: segment.parameters?.bbox || [0, 0, 0, 0],
        },
        review_state: segment.review_state,
      })) || [];

    const mentions: TSegmentMention[] =
      previewData.clip_mentions.map(mention => ({
        ...mention,
        segment_id: mention.segment_id,
        text: mention.text,
        start_datetime: mention.start_datetime,
        end_datetime: mention.end_datetime,
        created_at: mention.created_at,
        updated_at: mention.updated_at,
        keywords: mention.keywords || [],
        created_by: mention.created_by,
        updated_by: mention.updated_by,
      })) || [];

    setLogos(prev => mergePreviewEnrichments(prev, logos));
    setPeople(prev => mergePreviewEnrichments(prev, people));
    setStatements(prev => mergePreviewEnrichments(prev, statements));
    setMentions(mentions);
  }, [previewData]);

  const {
    hasNextItem,
    hasPreviousItem,
    handleGoToFirst,
    handleGoToLast,
    handleGoToNext,
    handleGoToPrevious,
  } = useNavigationList({
    activeItem: activeSegment,
    items: segmentsByClip,
    setActiveItem: setActiveSegment,
  });

  const handleEraseDescription = useCallback(() => {
    cutForm.setValue('description', '');
    setSelectedMention('');
  }, [cutForm, setSelectedMention]);

  const handleSetEndMark = useCallback(
    (datetime: Date) => cutForm.setValue('end_datetime', datetime),
    [cutForm]
  );

  // Leaving shouldFetchPreview latched here would fire the enrichment merge on
  // its own as soon as F9 restores the end mark.
  const handleClearEndMark = useCallback(() => {
    cutForm.setValue('end_datetime', null);
    cutForm.clearErrors('end_datetime');
    setShouldFetchPreview(false);
  }, [cutForm]);

  useCutFormShortcuts({
    segmentStartDatetime: activeSegment?.start_datetime,
    playerVideoCurrentTime,
    onExit: handleClickExit,
    onSetEnd: handleSetEndMark,
    onClearEnd: handleClearEndMark,
    onEraseDescription: handleEraseDescription,
  });

  useEffect(() => {
    if (!startTimeValue || !endTimeValue) return;
    cutForm.trigger('end_datetime');
  }, [startTimeValue, endTimeValue]);

  // Same rounding the saved instant goes through, so what is checked here is
  // exactly what would be written to the presence.
  const playerDatetime = useMemo(() => {
    if (!activeSegment?.start_datetime) return null;

    return getAbsoluteDateFromPlayerTime(activeSegment.start_datetime, playerVideoCurrentTime);
  }, [activeSegment?.start_datetime, playerVideoCurrentTime]);

  const addToIsDisabled = useMemo(
    () => isPresenceAddDisabled(startTimeValue, endTimeValue, playerDatetime),
    [playerDatetime, startTimeValue, endTimeValue]
  );

  const statementAddIsDisabled = isStatementAddDisabled(
    startTimeValue,
    endTimeValue,
    playerDatetime
  );

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const clipId = searchParams.get('clip_id');

  const handleClickExitSegment = () => {
    if (clipId) {
      //Resetar la url de limit y de clip_id
      searchParams.delete('clip_id');
      searchParams.delete('limit');

      window.history.replaceState({}, '', `?${searchParams.toString()}`);

      handleClickExit();
    } else {
      handleClickExit();
    }
  };

  return (
    <>
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between border-b-[1px] border-accent-disabled-10">
          <Typography
            fontSize={FontSizeEnum.Xl}
            color={FontColorEnum.Dark10}
            weight={FontWeightEnum.Medium}
          >
            {`${cutData ? t('Editar corte') : t('Crear nuevo corte')}`}
          </Typography>

          <IconButton
            size={IconButtonSizeEnum.LG}
            color={IconButtonColorEnum.NONE_DEFAULT}
            onClick={handleClickExitSegment}
          >
            <CloseIcon size={IconSizeEnum.LG} />
          </IconButton>
        </div>
        <div className={clsx('flex w-full', cutData ? 'justify-between' : 'justify-end')}>
          {cutData && (
            <div className="flex items-center gap-2 rounded-md bg-neutral-15 px-2 py-1 text-accent-active-primary-30">
              <div className="flex items-center gap-1">
                {hasPreviousItem && (
                  <Tooltip
                    help={<ToolTipContent text="Segmento anterior" keyword="To decide" />}
                    grow
                  >
                    <ChevronLeftIcon
                      onClick={handleGoToPrevious}
                      size={IconSizeEnum.SM}
                      className="cursor-pointer"
                    />
                  </Tooltip>
                )}
                <Typography
                  fontSize={FontSizeEnum.Md}
                  color={FontColorEnum.Medium20}
                  weight={FontWeightEnum.Regular}
                >
                  {segmentsByClip.findIndex(segment => segment.id === activeSegment?.id) + 1}/
                  {segmentsByClip.length}
                </Typography>
                {hasNextItem && (
                  <Tooltip
                    help={<ToolTipContent text="Segmento siguiente" keyword="To decide" />}
                    grow
                  >
                    <ChevronLeftIcon
                      onClick={handleGoToNext}
                      size={IconSizeEnum.SM}
                      className="-rotate-180 cursor-pointer"
                    />
                  </Tooltip>
                )}
              </div>
              {hasPreviousItem && (
                <Tooltip help={<ToolTipContent text="Primer segmento" keyword="To decide" />} grow>
                  <GoToOutIcon
                    onClick={handleGoToFirst}
                    size={IconSizeEnum.SM}
                    className="-rotate-180 cursor-pointer"
                  />
                </Tooltip>
              )}
              {hasNextItem && (
                <Tooltip help={<ToolTipContent text="Último segmento" keyword="To decide" />} grow>
                  <GoToOutIcon
                    onClick={handleGoToLast}
                    size={IconSizeEnum.SM}
                    className="cursor-pointer"
                  />
                </Tooltip>
              )}
              <Tooltip help={<ToolTipContent text="Borrar descripción" keyword="F4" />} grow>
                <EraseContentIcon
                  onClick={handleEraseDescription}
                  size={IconSizeEnum.SM}
                  className="cursor-pointer"
                />
              </Tooltip>
            </div>
          )}
          <div className="flex items-center justify-end gap-3">
            {cutData && (
              <Button
                onClick={() => previewModal.open()}
                color={ButtonColorEnum.DEFAULT}
                disabled={!endTimeValue}
              >
                {isLoadingPreview ? (
                  <>{t('Actualizando enriquecimientos')}...</>
                ) : (
                  t('Actualizar enriquecimientos')
                )}
              </Button>
            )}
            <StatusSeenChipSelect
              value={statusValue}
              onChange={handleChangeReviewState}
              reviewStates={SEGMENT_REVIEW_STATES}
            />
          </div>
        </div>
        <InputArea form={cutForm} name="description" required label={t('Descripción')} bigSize />
        {!isParrillaSegments && (
          <>
            <RowContainer className="!md:flex-wrap">
              <ProgramSelector
                programs={Array.from(
                  new Map(
                    segmentsByClip
                      .flatMap(segment => segment.programs)
                      .map(program => [program.id, program])
                  ).values()
                )}
                label={t('Programa')}
                required
                name="program_id"
                form={cutForm}
                disabled={!!cutData?.program_id}
              />
            </RowContainer>
            {segmentsByClip.flatMap(segment => segment.programs).length > 0 && (
              <Typography
                fontSize={FontSizeEnum.Md}
                color={FontColorEnum.Dark10}
                weight={FontWeightEnum.Regular}
              >
                {t('No existe programa en este rango temporal')}
              </Typography>
            )}
          </>
        )}
        <RowContainer>
          <InputDate
            form={cutForm}
            name="start_datetime"
            required
            label={t('Inicio')}
            seconds
            help
            tooltipHelp={t('Rellenar') as I18nextTranslationKeys}
            tooltipKeyword="F2"
            iconTooltip={ClockIcon}
            onClickTooltip={() => handleDateTimeSegment(cutForm, 'start_datetime')}
          />
          <InputDate
            form={cutForm}
            name="end_datetime"
            required
            label={t('Fin')}
            seconds
            requiredMessage={t('Marca el fin del corte con F9 antes de guardar')}
            validate={endDate => {
              // F8 leaves the field empty on purpose; `required` is what reports it.
              if (!endDate) return true;
              const startDate = cutForm.getValues('start_datetime');
              if (!startDate) return true;
              if (new Date(endDate) < new Date(startDate)) {
                return t('La fecha de fin no puede ser anterior a la de inicio');
              }
              // Only on creation: the backend rejects the emission mismatch in
              // create_clip_command, not in update, so blocking it while editing
              // would stop saves the API would have accepted.
              if (cutData) return true;
              if (!cutOverlapsBroadcast(startDate, new Date(endDate), broadcastWindow)) {
                return `${t('El corte debe estar dentro del horario de emisión')}: ${broadcastRangeLabel}`;
              }
              return true;
            }}
            help
            tooltipHelp={t('Rellenar') as I18nextTranslationKeys}
            tooltipKeyword="F9"
            iconTooltip={ClockIcon}
            onClickTooltip={() => handleDateTimeSegment(cutForm, 'end_datetime')}
          />
        </RowContainer>
        <RowContainer>
          <SelectForm
            form={cutForm}
            name="seccion"
            label={t('Temática')}
            required
            options={clipCategoriesOptions}
          />
        </RowContainer>
        {showTvSections && (
          <>
            <AddLogos
              logos={logos}
              setLogos={setLogos}
              disabled={addToIsDisabled}
              isLoadingPreview={isLoadingPreview}
              showWarningPresences={showWarningLogos}
              cutStart={startTimeValue}
              cutEnd={endTimeValue}
            />
            <hr />
            <AddPeople
              people={people}
              setPeople={setPeople}
              disabled={addToIsDisabled}
              isLoadingPreview={isLoadingPreview}
              showWarningPresences={showWarningPeople}
              cutStart={startTimeValue}
              cutEnd={endTimeValue}
            />
            <hr />
          </>
        )}
        <AddStatements
          statements={statements}
          setStatements={setStatements}
          showStatementSelect={showStatementSelect}
          setShowStatementSelect={setShowStatementSelect}
          showWarningStatements={showWarningStatements}
          cutStart={startTimeValue}
          cutEnd={endTimeValue}
          disabled={statementAddIsDisabled}
          isLoadingPreview={isLoadingPreview}
        />
        <hr />
        {/* Keywords */}
        <ShowMentions mentions={mentions} isLoadingPreview={isLoadingPreview} />
        <hr />
        {/* <ShowAssigments assigments={[]} /> */}
        {isNewCutWithoutChannel && isCutChannelError && (
          <Typography fontSize={FontSizeEnum.Sm} color={FontColorEnum.Error20}>
            {t('No se pudo cargar el canal')}
          </Typography>
        )}
        <FooterFormActions
          form={cutForm}
          handleCancel={handleClickExit}
          handleSubmit={handleSubmit}
          isLoading={isSubmitting || isNewCutWithoutChannel}
        />
      </div>

      {createPortal(
        <ModalDialog
          isOpen={previewModal.isOpen}
          onClose={previewModal.close}
          onAction={() => handleUpdateEnrichments()}
          heading={t('Actualizar enriquecimientos')}
          actionLabel={t('Actualizar')}
          linkLabel={t('Cancelar')}
          color={ModalMessageColorsEnum.WARNING}
        >
          <ModalMessage
            title={t('¿Estás seguro de actualizar los enriquecimientos?')}
            description={t(
              'Se actualizarán los enriquecimientos para que estén actualizados con los segmentos del corte'
            )}
            icon={WarningInfoIcon}
          />
        </ModalDialog>,
        document.body
      )}
    </>
  );
}
